import React, { useEffect, useRef, useState } from 'react';
import {
  SORT_OPTIONS, SORT_LABEL, DEFAULT_SORT_DIR, DEFAULT_SORTS,
  type SortKey, type SortEntry,
} from './clientViewTokens';

interface CarouselSortControlProps {
  sorts: SortEntry[];
  onChange: (sorts: SortEntry[]) => void;
  disabled?: boolean;
}

/** Sort options grouped for the <optgroup>s, in SORT_OPTIONS declaration order. */
const GROUPS: { group: string; keys: SortKey[] }[] = SORT_OPTIONS.reduce(
  (acc, o) => {
    const last = acc[acc.length - 1];
    if (last && last.group === o.group) last.keys.push(o.key);
    else acc.push({ group: o.group, keys: [o.key] });
    return acc;
  },
  [] as { group: string; keys: SortKey[] }[],
);

const shortLabel = (key: SortKey) => SORT_LABEL[key].replace(' (default)', '');

/**
 * Multi-field carousel sort. Every card metric, priority bucket and tier is
 * available (they would not fit as individual chips), and the fields chain in
 * list order — the first one that separates two dealers wins — each carrying
 * its OWN ascending/descending direction.
 */
export const CarouselSortControl: React.FC<CarouselSortControlProps> = ({
  sorts, onChange, disabled,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const used = new Set(sorts.map((s) => s.key));
  const summary = sorts.length === 0
    ? 'none'
    : shortLabel(sorts[0].key) + (sorts.length > 1 ? ` +${sorts.length - 1}` : '');

  const replaceAt = (i: number, entry: SortEntry) =>
    onChange(sorts.map((s, idx) => (idx === i ? entry : s)));

  const move = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= sorts.length) return;
    const next = [...sorts];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="cv-sort-control" ref={wrapRef}>
      <button
        type="button"
        className={`cv-sort-btn${sorts.length > 1 ? ' active' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        title="Choose the fields the carousel sorts by"
      >
        Sort: {summary} ▾
      </button>
      {open && (
        <div className="cv-sort-menu" role="dialog" aria-label="Carousel sort fields">
          <div className="cv-sort-menu-head">
            <span>Sort by, in order</span>
            <button type="button" className="cv-sort-reset" onClick={() => onChange(DEFAULT_SORTS)}>
              Reset
            </button>
          </div>

          {sorts.length === 0 ? (
            <div className="cv-sort-empty">No sort — dealers are listed by name.</div>
          ) : (
            <ol className="cv-sort-rows">
              {sorts.map((entry, i) => (
                <li key={entry.key} className="cv-sort-row">
                  <span className="cv-sort-rank">{i + 1}</span>
                  <span className="cv-sort-name" title={shortLabel(entry.key)}>{shortLabel(entry.key)}</span>
                  <button
                    type="button"
                    className="cv-sort-dir"
                    onClick={() => replaceAt(i, { ...entry, dir: entry.dir === 'desc' ? 'asc' : 'desc' })}
                    title={entry.dir === 'desc' ? 'Highest first — click for lowest first' : 'Lowest first — click for highest first'}
                    aria-label={`${shortLabel(entry.key)}: sorted ${entry.dir === 'desc' ? 'descending' : 'ascending'}`}
                  >
                    {entry.dir === 'desc' ? '↓' : '↑'}
                  </button>
                  <button
                    type="button"
                    className="cv-sort-move"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    aria-label={`Move ${shortLabel(entry.key)} earlier`}
                    title="Apply this field earlier"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="cv-sort-move"
                    onClick={() => move(i, 1)}
                    disabled={i === sorts.length - 1}
                    aria-label={`Move ${shortLabel(entry.key)} later`}
                    title="Apply this field later"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="cv-sort-remove"
                    onClick={() => onChange(sorts.filter((_, idx) => idx !== i))}
                    aria-label={`Remove ${shortLabel(entry.key)} from the sort`}
                    title="Remove"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ol>
          )}

          <select
            className="cv-sort-add"
            value=""
            aria-label="Add a sort field"
            onChange={(e) => {
              const key = e.target.value as SortKey;
              if (key) onChange([...sorts, { key, dir: DEFAULT_SORT_DIR[key] }]);
            }}
          >
            <option value="">+ Add sort field…</option>
            {GROUPS.map(({ group, keys }) => {
              const available = keys.filter((k) => !used.has(k));
              if (available.length === 0) return null;
              return (
                <optgroup key={group} label={group}>
                  {available.map((k) => <option key={k} value={k}>{shortLabel(k)}</option>)}
                </optgroup>
              );
            })}
          </select>
        </div>
      )}
    </div>
  );
};
